import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ContentBundle,
  CurrentUser,
  NextActivity,
  TaskDefinition,
} from "@talent/protocol";
import {
  IdbOutboxStore,
  MemoryOutboxStore,
  SessionRecorder,
  requestPersistentStorage,
  type OutboxStore,
} from "@talent/client-telemetry";
import { APP_VERSION, describeDevice } from "./env.js";
import { HttpTransport, fetchAssignedBundle } from "./api.js";
import { fetchMe } from "./auth-api.js";
import { fetchMyGames, fetchMyNext } from "./class-api.js";
import { COPY, fill } from "./copy.js";
import { fa } from "./format.js";
import { runGame, type LastResult } from "./game-runners.js";
import { HomeScreen } from "./home-screen.js";
import { TutorialPrompt, type TutorialChoice } from "./tutorial-prompt.js";
import { ACCENT, familyLook } from "./accent.js";
import { Creature } from "./creature.js";
import { useBackNavigation } from "./back-navigation.js";
import { setSystemBarColors } from "./app-shell-bridge.js";
import { COLOR, css } from "./paint.js";
import { isPrivate, isTeacherOnly, type Route } from "./route.js";
import { useRoute } from "./use-route.js";
import { ApplyScreen } from "./enter/apply-screen.js";
import { AuthScreen } from "./enter/auth-screen.js";
import { ClassProvider } from "./teacher/class-context.js";
import { ClassesScreen } from "./teacher/classes.js";
import { GamesScreen } from "./teacher/games.js";
import { ProfileScreen } from "./teacher/profile.js";
import { nextForm } from "./profile.js";
import { CompareScreen } from "./teacher/compare.js";
import { GoalsScreen } from "./teacher/goals.js";
import { PreviewSurface } from "./teacher/preview.js";
import { RosterScreen } from "./teacher/roster.js";
import { StudentScreen } from "./teacher/student.js";
import { AboutPage } from "./site/about.js";
import { LandingPage } from "./site/landing.js";
import { SiteFooter, SiteHeader, SitePage } from "./site/shell.js";

/**
 * The boot lifecycle only — NOT which screen is showing.
 *
 * Conflating the two is what produced a blank screen on back: the state said "playing" while the
 * route said "home", the canvas had already been torn down, and no branch matched. Which screen
 * shows is derived from the route below, because the route is the thing the browser, the back
 * button and a reload all agree on.
 */
type Status = "booting" | "ready" | "failed";

/**
 * The outbox for one learner.
 *
 * **Named after the child, not the browser.** A tablet is shared: a child plays, some events are still
 * queued, and then a teacher signs in on the same device. With one outbox for the browser that queue
 * flushes under the teacher's session, the server refuses it (R-3), and nothing ever drains it — so
 * every later flush fails too and the child's trials are stranded behind a repeating 403. Per learner,
 * the queue is simply invisible to anybody else and waits for them to come back.
 *
 * IndexedDB is the norm; the memory store is a last resort so a session can still be played and
 * partially delivered rather than refused outright.
 */
function pickStore(profileId: string): OutboxStore {
  try {
    if (typeof indexedDB !== "undefined") return IdbOutboxStore.forProfile(profileId);
  } catch {
    /* fall through */
  }
  return new MemoryOutboxStore();
}

/**
 * The site: public pages, one sign-in, and two private surfaces behind it — one per role.
 *
 * **Everyone signs in, and which surface they land on is the server's answer rather than a choice
 * on screen** (ADR-011). A number already on a class roster is a student; any other is a teacher.
 * Asking would be a question that could be answered wrongly, and a child cannot be trusted to
 * answer it at all.
 *
 * `PlaySurface` is mounted only for a student. Remounting it starts a new recorder, and a new
 * recorder emits a `session_start` — so anything that unmounts and remounts it fabricates a play
 * session in a child's record.
 */
export function App() {
  const [route, navigate, redirect] = useRoute();
  const [session, setSession] = useState<SessionState>({ kind: "unknown" });

  // Asked once on load. Everything downstream reads this rather than probing again, so there is
  // one answer to "is anyone signed in" and no two parts of the UI can disagree about it.
  useEffect(() => {
    let cancelled = false;
    void fetchMe()
      .then((me) => {
        if (cancelled) return;
        setSession(me ? { kind: "in", user: me } : { kind: "out" });
      })
      .catch(() => {
        // Unreachable server is not signed out: showing a sign-in page to someone who has a valid
        // session would ask them to sign in twice and, on failure, look like their account is gone.
        if (!cancelled) setSession({ kind: "unreachable" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signedIn = session.kind === "in";
  const settled = session.kind !== "unknown";
  const user = session.kind === "in" ? session.user : null;
  const isTeacher = user?.role === "teacher";
  const isStudent = user?.role === "student";
  /**
   * An account this app has no screen for.
   *
   * Today that is only an admin (ADR-013): a real, signed-in account that is neither of the two
   * roles this app knows how to show. Left unchecked, `showPlay` below reads `!isTeacher` as
   * "student" and un-hides the play container for it too, and `PlaySurface`'s own
   * `role === "student"` guard then renders nothing inside it — a signed-in account with a blank
   * screen and no error anywhere, which is indistinguishable from broken.
   */
  const isUnsupportedAccount = signedIn && user != null && !isTeacher && !isStudent;
  /** Where this account belongs when it has nowhere particular to be. */
  const home: Route = isTeacher ? { kind: "class" } : { kind: "play" };

  // The hardware back key, and the browser's. Plain history everywhere except Kiddora's own
  // roots — see `back-navigation.ts`.
  useBackNavigation(route);

  /**
   * Forgetting the session and going somewhere public.
   *
   * One helper because three surfaces need it and the ORDER matters: clearing the session first would
   * let the private-route guard fire and navigate to `#/enter` before the intended destination, so a
   * child signing out would land on a sign-in screen instead of the landing page.
   */
  const signOutTo = useCallback(
    (destination: "landing" | "enter") => () => {
      navigate({ kind: destination });
      setSession({ kind: "out" });
    },
    [navigate],
  );

  /**
   * A private route while signed out lands on sign-in rather than rendering nothing.
   *
   * Done in an effect rather than by rendering the sign-in page in place, so the URL matches what
   * is on screen — otherwise a reload after signing in would return to `#/parent` having shown
   * something else, and the back button would walk through screens that were never visited.
   */
  useEffect(() => {
    if (settled && !signedIn && isPrivate(route)) redirect({ kind: "enter" });
  }, [settled, signedIn, route, redirect]);

  /**
   * A child who reaches a class URL is sent to the games.
   *
   * The server refuses them regardless, and that is the check that matters — this one exists so a
   * mistyped or shared link lands somewhere a five-year-old can use, rather than on a screen that
   * then fails four requests in front of them.
   */
  useEffect(() => {
    if (signedIn && !isTeacher && isTeacherOnly(route)) redirect({ kind: "play" });
  }, [signedIn, isTeacher, route, redirect]);

  if (!settled) {
    return (
      <div className="grid min-h-full place-items-center p-xl">
        <Creature kind="firefly" accent="sunny" size={84} />
      </div>
    );
  }

  const showPlay = signedIn && isStudent && (route.kind === "play" || route.kind === "game");

  return (
    <>
      {(route.kind === "landing" ||
        route.kind === "about" ||
        route.kind === "enter" ||
        route.kind === "apply") && (
        <div className="flex min-h-full flex-col">
          <SiteHeader navigate={navigate} home={signedIn ? home : null} />
          <div className="flex-1">
            {route.kind === "landing" && <LandingPage navigate={navigate} home={signedIn ? home : null} />}
            {route.kind === "about" && <AboutPage navigate={navigate} home={signedIn ? home : null} />}
            {route.kind === "enter" && (
              <SitePage>
                <AuthScreen
                  onSignedIn={(signedInUser) => {
                    /**
                     * Straight to wherever this account belongs.
                     *
                     * The role comes back with the verification, so there is no second request and
                     * no intermediate screen — a teacher lands on their class and a child on the
                     * games, which is the only thing either of them came here for.
                     */
                    setSession({ kind: "in", user: signedInUser });
                    navigate(signedInUser.role === "teacher" ? { kind: "class" } : { kind: "play" });
                  }}
                  onApply={() => navigate({ kind: "apply" })}
                />
              </SitePage>
            )}
            {/*
              * Registering as a teacher — its own route, so it survives a reload and has its own back
              * target, and public like sign-in because the whole point is that no account exists yet.
              *
              * NOT wrapped in `SitePage`, unlike every other public screen. That wrapper is a 720px
              * reading column, which is the right shape for a sign-in card and the wrong one for a page
              * whose form sits beside what it is asking for (ADR-012). This screen owns its own width.
              */}
            {route.kind === "apply" && <ApplyScreen onSignIn={() => navigate({ kind: "enter" })} />}
          </div>
          <SiteFooter />
        </div>
      )}

      <div className={showPlay ? "contents" : "hidden"}>
        {user && user.role === "student" && user.profileId && (
          <PlaySurface
            route={route}
            navigate={navigate}
            profileId={user.profileId}
            onSignOut={signOutTo("landing")}
          />
        )}
      </div>

      {/*
        * A signed-in account this app has no surface for — an admin today (ADR-013).
        *
        * Said plainly and pointed at the panel it belongs on, rather than left to fall through to
        * the "nowhere to be" fallback below: that one's button sends an account HOME within this
        * app, which for an admin is nowhere too and would just repeat the same dead end.
        */}
      {isUnsupportedAccount && isPrivate(route) && (
        <div className="grid h-full place-items-center gap-md p-xl text-center">
          <Creature kind="cat" accent="watermelon" size={88} />
          <p className="text-body text-ink">{COPY.enter.wrongApp.body}</p>
          <button
            onClick={signOutTo("landing")}
            className="rounded-pill bg-card px-lg py-md text-body shadow-card"
          >
            {COPY.enter.wrongApp.signOut}
          </button>
        </div>
      )}

      {/*
        * The teacher's surface, wrapped in the class it is scoped to.
        *
        * The provider sits OUTSIDE the screens rather than inside each one, so switching class does not
        * unmount the provider and lose the choice — and so the choice is one value rather than five
        * copies that can disagree (ADR-012).
        */}
      {isTeacher && isTeacherOnly(route) && user && (
        <ClassProvider initial={user.classes} initialAllowance={user.allowance}>
          <TeacherSurface
            route={route}
            navigate={navigate}
            user={user}
            onSignedOut={signOutTo("enter")}
          />
        </ClassProvider>
      )}

      {/*
        * A signed-in account with nowhere to be.
        *
        * A teacher on `#/play`, or a student whose learner record is missing. Both are recoverable
        * by going home, and both are silent bugs if left to render nothing at all.
        */}
      {signedIn &&
        isPrivate(route) &&
        !showPlay &&
        !isUnsupportedAccount &&
        !(isTeacher && isTeacherOnly(route)) && (
        <div className="grid h-full place-items-center p-xl">
          <button
            onClick={() => navigate(home)}
            className="rounded-pill bg-card px-lg py-md text-body shadow-card"
          >
            {COPY.child.backToGames}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * The teacher's four screens, chosen by route.
 *
 * Separated from `App` so the roster's own state does not live in the component that owns boot and
 * routing — and so navigating between class screens remounts only the screen, which is what makes a
 * return to the roster re-read the class rather than show a stale one.
 */
function TeacherSurface({
  route,
  navigate,
  user,
  onSignedOut,
}: {
  route: Route;
  navigate: (next: Route) => void;
  user: CurrentUser;
  onSignedOut: () => void;
}) {
  const openStudent = (studentId: string) => navigate({ kind: "student", studentId });
  // Plain history back — the roster is genuinely where every one of these screens was opened
  // from, so there is nothing to compute.
  const toClass = () => window.history.back();

  if (route.kind === "student") {
    return (
      <StudentScreen
        studentId={route.studentId}
        onBack={toClass}
        onSignedOut={onSignedOut}
        // This screen is about a student who no longer exists, so it cannot stay open.
        onRemoved={toClass}
      />
    );
  }
  if (route.kind === "classes") {
    return <ClassesScreen onBack={toClass} />;
  }
  if (route.kind === "classGames") {
    return <GamesScreen onBack={toClass} onSignedOut={onSignedOut} />;
  }
  if (route.kind === "profile") {
    return <ProfileScreen user={user} onBack={toClass} onSignOut={onSignedOut} />;
  }
  if (route.kind === "goals") {
    return (
      <GoalsScreen onOpenStudent={openStudent} onBack={toClass} onSignedOut={onSignedOut} />
    );
  }
  if (route.kind === "compare") {
    return (
      <CompareScreen onOpenStudent={openStudent} onBack={toClass} onSignedOut={onSignedOut} />
    );
  }
  if (route.kind === "preview" || route.kind === "previewGame") {
    return <PreviewSurface route={route} navigate={navigate} />;
  }
  return (
    <RosterScreen
      onOpenStudent={openStudent}
      onOpenGoals={() => navigate({ kind: "goals" })}
      onOpenCompare={() => navigate({ kind: "compare" })}
      onOpenPreview={() => navigate({ kind: "preview" })}
      onOpenGames={() => navigate({ kind: "classGames" })}
      onOpenClasses={() => navigate({ kind: "classes" })}
      onOpenProfile={() => navigate({ kind: "profile" })}
      onSignedOut={onSignedOut}
    />
  );
}

/** Whether anyone is signed in. `unreachable` is deliberately not "signed out" — see above. */
type SessionState =
  | { kind: "unknown" }
  | { kind: "out" }
  | { kind: "unreachable" }
  | { kind: "in"; user: CurrentUser };

function PlaySurface({
  route,
  navigate,
  profileId,
  onSignOut,
}: {
  route: Route;
  navigate: (next: Route) => void;
  /** The learner record this child's events are written against, from their own session. */
  profileId: string;
  onSignOut: () => void;
}) {
  const [status, setStatus] = useState<Status>("booting");
  const [error, setError] = useState<string | null>(null);
  const [bundle, setBundle] = useState<ContentBundle | null>(null);
  const [lastResult, setLastResult] = useState<LastResult | null>(null);
  /**
   * Set while a game is holding after its demonstration, and resolved when the child taps start.
   *
   * The renderer decides WHEN the demonstration is over; the shell decides how to ask. That is why
   * the prompt can be a real dialog with the design tokens instead of a glyph painted into
   * whatever corner of the canvas happened to be free.
   */
  const [awaitingTutorial, setAwaitingTutorial] = useState<
    ((choice: TutorialChoice) => void) | null
  >(null);
  /** The task just played, so the reward screen shows that game's creature and colour. */
  const [lastTask, setLastTask] = useState<TaskDefinition | null>(null);
  /**
   * What to play next, and why. Fetched alongside the bundle.
   *
   * Null until it arrives, so the shelf appears immediately — a child must never wait on a request
   * they did not make, and the free-choice games are usable the whole time.
   */
  const [next, setNext] = useState<NextActivity | null>(null);
  /** The games this child's class may play. Empty until the answer arrives. */
  const [openTasks, setOpenTasks] = useState<readonly string[]>([]);
  const recorderRef = useRef<SessionRecorder | null>(null);
  /** Games finished in this app session, for the `session_end` count. */
  const tasksCompletedRef = useRef(0);
  const playAreaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        /**
         * The learner record comes from the session, not from this device.
         *
         * It used to be created here on first launch and kept in local storage with a write grant,
         * because a child had no account. Now a student signs in, so the record already exists — a
         * teacher made it — and losing this device loses nothing (ADR-011).
         */

        // Ask the server which bundle to run. Never "latest" (ADR-002).
        const assignment = await fetchAssignedBundle(profileId);
        if (cancelled) return;

        const store = pickStore(profileId);
        const persisted = await requestPersistentStorage();
        const device = await describeDevice();

        const recorder = new SessionRecorder({
          sessionId: crypto.randomUUID(),
          profileId,
          appVersion: APP_VERSION,
          pinnedBundleVersion: assignment.bundleVersion,
          store,
          transport: new HttpTransport(),
        });
        recorder.attachLifecycle(document);
        recorderRef.current = recorder;

        /**
         * `session_end` fires when the APP session ends, not when a game does.
         *
         * A recorder holds one sessionId for the whole visit, so recording an end after each game
         * emitted several ends for one session. And `attachLifecycle` flushes on pagehide without
         * recording an end at all, so sessions were left with a null `endedAt` — which is what
         * the previous arrangement actually produced once games stopped ending the session.
         *
         * The reason is "abandoned" because that is what we know: a hidden page is
         * indistinguishable from a child wandering off, and claiming "completed" would be a guess
         * recorded as a fact.
         */
        window.addEventListener("pagehide", () => {
          void recorder
            .record("session_end", {
              reason: "abandoned",
              tasksCompleted: tasksCompletedRef.current,
            })
            .then(() => recorder.flush());
        });

        await recorder.record("session_start", {
          device,
          storagePersisted: persisted.granted,
          // No adult-supervision question is asked yet; claiming to know would be
          // worse than recording that we do not.
          declaredPlayer: "unknown",
          pinnedBundleVersion: assignment.bundleVersion,
        });
        await recorder.record("storage_persist_result", {
          granted: persisted.granted,
          estimateBytes: persisted.estimateBytes,
        });

        setBundle(assignment.bundle);
        setStatus("ready");

        /**
         * The suggestion, after the games are already usable.
         *
         * Deliberately not awaited with the bundle: a slow or failing suggestion must not keep a
         * child out of the shelf. A failure leaves the card absent, which is the same as having
         * nothing to suggest.
         */
        void fetchMyNext()
          .then((suggestion) => {
            if (!cancelled) setNext(suggestion);
          })
          .catch(() => undefined);

        /**
         * Which games this class has been given (ADR-014).
         *
         * **Awaited before the shelf is shown, unlike the suggestion.** A shelf drawn without it would
         * show every game for a moment and then take most of them away, and a four-year-old who tapped
         * during that moment would play a whole sitting the server then refuses.
         *
         * A failure leaves the empty set, which is the safe direction: no game rather than a game that
         * was not given. The screen says the teacher has not opened anything yet, which is also what a
         * genuinely empty class looks like.
         */
        const open = await fetchMyGames().catch(() => [] as string[]);
        if (!cancelled) setOpenTasks(open);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("failed");
      }
    })();

    return () => {
      cancelled = true;
      void recorderRef.current?.flush();
    };
  }, [profileId]);

  /** Resolves when the child dismisses the dialog, or immediately if they leave the game. */
  const holdForTutorial = useCallback(
    () =>
      new Promise<TutorialChoice>((resolve) => setAwaitingTutorial(() => resolve)),
    [],
  );

  /**
   * What the current game is FOR, when the child arrived through the suggestion card.
   *
   * A ref rather than state: it is read once inside `play`, and holding it in state would restart
   * the run effect every time it changed.
   */
  const forObjective = useRef<NextActivity | null>(null);

  const play = useCallback(async (task: TaskDefinition, signal: AbortSignal) => {
    const recorder = recorderRef.current;
    const parent = playAreaRef.current;
    if (!recorder || !parent) return;

    setLastTask(task);
    // Cleared so a previous game's reward screen cannot flash over a new game's first trial.
    setLastResult(null);
    try {
      /**
       * The objective travels into `task_start`, and only when it applies to THIS game.
       *
       * Cleared afterwards, so a child who finishes the suggested activity and then picks something
       * themselves has that recorded as free play — which is the distinction the whole
       * "who practised what" query rests on (ADR-011).
       */
      const intent = forObjective.current?.taskId === task.id ? forObjective.current : null;
      forObjective.current = null;

      const result = await runGame({
        canvasParent: parent,
        task,
        formId: nextForm(task.id, task.measurement.formIds),
        recorder,
        signal,
        holdForTutorial,
        ...(intent?.objectiveId ? { objectiveId: intent.objectiveId } : {}),
        ...(intent?.startAt !== undefined && intent.startAt !== null
          ? { startAt: intent.startAt }
          : {}),
      });

      await recorder.flush();

      // A child who left goes back to the list without a reward screen. Congratulating someone
      // for a game they walked out of is the kind of small dishonesty that makes a product feel
      // like it is not paying attention.
      if (signal.aborted) return;

      tasksCompletedRef.current += 1;
      setLastResult(result);
    } catch (err) {
      if (signal.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus("failed");
    }
  }, []);

  /**
   * The route starts and stops games.
   *
   * Driven by the URL rather than by a click handler, which is what makes the device back button
   * leave a game instead of leaving the app, and makes a reload during a game return to that
   * game. Leaving aborts the run, so the task records `task_end` with `reason: "abandoned"`
   * instead of leaving a trial open forever — that reason was in the result type from the start
   * and, until the abort existed, nothing could produce it.
   */
  const runningRef = useRef<{
    taskId: string;
    controller: AbortController;
    /** Settles when this run has fully torn down. The next game waits on it. */
    done: Promise<void>;
  } | null>(null);

  useEffect(() => {
    const wanted = route.kind === "game" ? route.taskId : null;
    const previous = runningRef.current;

    if (previous && previous.taskId !== wanted) {
      previous.controller.abort();
      runningRef.current = null;
      // A game abandoned while its dialog was open would otherwise leave the renderer awaiting a
      // tap that can no longer happen, and its teardown would never run.
      setAwaitingTutorial((resolve: ((choice: TutorialChoice) => void) | null) => {
        // Starting rather than replaying: the child has left, so the demonstration must not loop.
        resolve?.("start");
        return null;
      });
    }
    if (!wanted || !bundle) return;
    // Guard against a second start: React re-runs effects in development, and two runs would
    // attach two Pixi canvases to the same element and record two overlapping task_starts.
    if (runningRef.current?.taskId === wanted) return;

    const task = bundle.tasks.find((t) => t.id === wanted);
    if (!task) {
      // A link to a game that is not in the pinned bundle. Home is the right answer, not an error
      // screen — the profile may simply be pinned to a bundle that never had it.
      navigate({ kind: "play" });
      return;
    }

    const controller = new AbortController();
    /**
     * The previous game is awaited before this one starts, and that ordering is the fix for the
     * previous game still being active after switching.
     *
     * Aborting only unblocks the old loop; its teardown is asynchronous — it still records
     * `task_end` and then destroys its Pixi application, which is what removes its canvas from
     * the shared play area. Starting the next game immediately meant two live canvases stacked on
     * the same element with both sets of pointer handlers attached, and worse: both runs share one
     * recorder, so the old run's `exitTask()` could land after the new run's `task_start` and
     * leave the new game's events recorded with no task at all.
     */
    const done = (async () => {
      if (previous) {
        // A failed previous run must not prevent the next game from starting.
        await previous.done.catch(() => undefined);
      }
      // The child may have navigated again while we waited.
      if (controller.signal.aborted) return;
      await play(task, controller.signal);
    })();

    runningRef.current = { taskId: wanted, controller, done };
  }, [route, bundle, play, navigate]);

  // Aborts an in-flight game if the whole surface goes away, so a task never outlives its canvas.
  useEffect(() => () => runningRef.current?.controller.abort(), []);

  /**
   * The whole child surface, laid out as three states rather than one screen with a footer.
   *
   * The previous version was a header showing a content-version string, an empty play area, and a
   * column of identical blue rectangles under it — a child saw a form. The game canvas now takes
   * the entire screen while playing, and the home screen is a shelf of things to touch.
   *
   * The play area stays MOUNTED in every state and is only hidden, because Pixi attaches its
   * canvas to that element; unmounting it between games would tear the canvas out from under a
   * renderer that is still finishing its teardown.
   */
  const look = lastTask ? familyLook(lastTask.family) : null;
  // The reward screen wears the accent of the game just played, so the celebration belongs to it.
  const accent = ACCENT[look?.accent ?? "sunny"];

  /**
   * Which screen, derived rather than stored.
   *
   * `playing` and `done` are both the game route: the difference is only whether a result has
   * come back yet. Deriving it means leaving a game cannot leave the UI in a state no branch
   * renders, which is the bug this replaces.
   */
  const screen =
    status === "booting"
      ? "boot"
      : status === "failed"
        ? "error"
        : route.kind === "game"
          ? // Only THIS game's result counts as done.
            lastResult?.taskId === route.taskId
            ? "done"
            : "playing"
          : "list";

  // The game canvas paints the dark stage itself, but the phone's own chrome — the browser/PWA
  // status bar and the OS gesture-nav strip below it — takes its colour from `theme-color`, which
  // is otherwise a static cream set once in index.html. Left alone, that strip stays cream under a
  // dark game, which is the "white bar under most games" a child sees on a phone. Kept in sync with
  // the actual stage colour used elsewhere in the canvas via `paint.ts`'s tokens, not a duplicate.
  useEffect(() => {
    const playing = screen === "playing" || screen === "done";
    const color = css(playing ? COLOR.stageDeep : COLOR.cream);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", color);
    setSystemBarColors(color, !playing);
  }, [screen]);

  return (
    <div className="relative h-full">
      <div
        ref={playAreaRef}
        // Visible only while a game is running, so the canvas cannot sit behind the home screen
        // intercepting taps. `visibility` rather than `display`, because Pixi measures this
        // element and a display:none host reports zero.
        className={`absolute inset-0 touch-manipulation ${
          screen === "playing" ? "visible" : "invisible"
        }`}
      />

      {/*
        Back out of a game.
        
        Placed over the canvas rather than in a chrome bar, because the game owns the whole screen
        and a permanent bar would cost the play area height on the small phones this has to work
        on. Inset from the corner so a child resting a thumb on the edge does not leave by
        accident, and it carries the game's own colour so it reads as part of that world.
      */}
      {screen === "playing" && (
        <button
          onClick={() => window.history.back()}
          aria-label={COPY.child.backToGames}
          // Translucent over the dark stage: legible without covering the board.
          className="top-safe-lg absolute start-lg z-2 grid size-14 place-items-center rounded-pill bg-card/16 text-card backdrop-blur-[8px]"
        >
          {/*
            An SVG, not a character. `›` is in the Unicode bidi mirroring set, so inside an RTL
            element the browser flips it and the glyph points the opposite way from the one
            written — which is exactly how it ended up backwards. An SVG has no bidi behaviour.

            It points right because the button sits at the inline start, which in RTL is the right
            edge, and that is where back lives on both iOS and Android in right-to-left layouts.
          */}
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M9 5l7 7-7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {screen === "boot" && (
        <div className="grid h-full place-items-center gap-lg">
          {/* A creature rather than a spinner: the first thing a child sees should be alive. */}
          <Creature kind="firefly" accent="sunny" size={96} />
          <p className="text-body text-muted">{COPY.child.preparing}</p>
        </div>
      )}

      {screen === "error" && (
        <div className="grid h-full place-items-center gap-md p-xl text-center">
          <Creature kind="cat" accent="watermelon" size={88} />
          {/* Deliberately not retried silently: a failure to reach the server means events are
              queued, and pretending otherwise would hide data loss. */}
          <p className="text-body text-ink">{fill(COPY.child.failed, { message: error ?? "" })}</p>
        </div>
      )}

      {screen === "list" && bundle && (
        <HomeScreen
          bundle={bundle}
          profileId={profileId}
          openTasks={openTasks}
          next={next}
          onPlayNext={(task, suggestion) => {
            forObjective.current = suggestion;
            navigate({ kind: "game", taskId: task.id });
          }}
          onSignOut={onSignOut}
          onPlay={(task, offered) => {
            /**
             * The one signal a parent actually wants, and it was never being written down.
             *
             * The database held 388 `visibility_change` events for this child and zero
             * `choice_made` — the machinery of measurement recorded exhaustively, and which game
             * they chose recorded not once. Interest needs no norms and no calibration; it only
             * needs somebody to save it.
             *
             * Fire-and-forget: a child must never wait on telemetry to start a game, and the
             * outbox retries on its own.
             */
            void recorderRef.current?.record("choice_made", { offered: [...offered], chosen: task.id });
            navigate({ kind: "game", taskId: task.id });
          }}
        />
      )}

      {/*
        * The dialog a game holds on after demonstrating itself.
        *
        * Rendered here rather than by the renderer, because the renderer draws on a canvas and this
        * needs to be a real dialog. The renderer decides WHEN by awaiting `onTutorialComplete`; this
        * decides how to ask, and resolving the promise is what lets the game start.
        */}
      {awaitingTutorial && lastTask && (
        <TutorialPrompt
          family={lastTask.family}
          onChoose={(choice) => {
            /**
             * Watching the demonstration again is recorded, and nothing recorded it before.
             *
             * `replay_requested` has been in the protocol since the first bundle with no emitter, so a
             * child asking to see a game explained twice — which is the clearest signal there is that
             * a game is not landing — left no trace at all. The renderer decides when to ask; this is
             * where the answer arrives, so this is where it belongs.
             *
             * Fire-and-forget: a child must never wait on telemetry, and the outbox retries.
             */
            if (choice === "replay") {
              void recorderRef.current?.record("replay_requested", { ofTaskId: lastTask.id });
            }
            awaitingTutorial(choice);
            setAwaitingTutorial(null);
          }}
        />
      )}

      {screen === "done" && lastResult && (
        <div className="grid h-full content-center place-items-center gap-lg p-xl text-center">
          {/* The creature from the game just played, so the reward belongs to that game. */}
          <Creature kind={look?.creature ?? "star"} accent={look?.accent ?? "sunny"} size={120} />
          {/* A raw task-local count. Not a construct estimate and not a comparison against other
              children — anything stronger needs the claims lexicon and its gates (v2 §21), and
              that is a parent-facing surface, not this one. */}
          <p className="text-title">
            {lastResult.scoring === "span"
              ? fill(COPY.child.spanDone, { best: fa(lastResult.best) })
              : fill(COPY.child.patternDone, {
                  correct: fa(lastResult.correct),
                  total: fa(lastResult.total),
                })}
          </p>
          <button
            onClick={() => window.history.back()}
            className={`min-h-touch rounded-pill px-xl py-md text-heading text-ink ${accent.bg} ${accent.shadow}`}
          >
            {COPY.child.playAgain}
          </button>
        </div>
      )}
    </div>
  );
}
